/**
 * Leviathan Web - Low Tank Alerts Widget
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows tanks below reorder point / low utilization
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Fuel } from 'lucide-react';
import { API_URL } from '@/config/api';

interface TankData {
  id: string;
  tankNumber: string;
  name: string;
  capacity: number;
  currentLevel: number;
  utilizationPercent: number;
  product: { name: string } | null;
}

const LowTankAlertsWidget = () => {
  const { accessToken } = useAuth();
  const [tanks, setTanks] = useState<TankData[]>([]);
  const [overallUtilization, setOverallUtilization] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          const allTanks: TankData[] = data.dashboard?.tanks?.data ?? [];
          // Show tanks below 30% utilization
          const lowTanks = allTanks.filter((t) => t.utilizationPercent < 30);
          setTanks(lowTanks);
          setOverallUtilization(data.dashboard?.tanks?.utilization ?? 0);
        }
      } catch {
        // silent
      }
    };
    fetchData();
  }, [accessToken]);

  const levelColor = (pct: number) =>
    pct < 20 ? 'bg-error-red' : pct < 40 ? 'bg-warning-amber' : 'bg-success-green';

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Fuel className="w-4 h-4 text-amber" />
          <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
            Low Tank Alerts
          </span>
        </div>
        <Link to="/tanks" className="text-[10px] text-text-tertiary hover:text-amber font-mono">
          View All &rarr;
        </Link>
      </div>

      {/* Overall utilization */}
      <div className="mb-3 pb-3 border-b border-panel-border">
        <div className="flex justify-between text-[10px] font-mono">
          <span className="text-text-secondary">Overall Utilization</span>
          <span className="text-amber">{overallUtilization}%</span>
        </div>
        <div className="mt-1.5 h-2 bg-background rounded-sm overflow-hidden">
          <div
            className={`h-full ${levelColor(overallUtilization)}`}
            style={{
              width: `${overallUtilization}%`,
              boxShadow: '0 0 6px currentColor',
            }}
          />
        </div>
      </div>

      {/* Low tanks */}
      <div className="space-y-2 overflow-y-auto flex-1">
        {tanks.length === 0 ? (
          <div className="flex items-center justify-center py-4">
            <span className="text-xs font-mono text-success-green">All tanks above 30%</span>
          </div>
        ) : (
          tanks.map((tank) => (
            <div
              key={tank.id}
              className="flex items-center space-x-3 py-1.5 border-b border-panel-border last:border-0"
            >
              <div className="w-16 text-[10px] font-mono text-text-secondary truncate">
                {tank.tankNumber}
              </div>
              <div className="flex-1 h-2.5 bg-background rounded-sm overflow-hidden relative">
                <div
                  className={`h-full ${levelColor(tank.utilizationPercent)} transition-all`}
                  style={{
                    width: `${tank.utilizationPercent}%`,
                    boxShadow: '0 0 6px currentColor',
                  }}
                />
              </div>
              <div className="w-10 text-right text-[10px] font-mono text-error-red">
                {Math.round(tank.utilizationPercent)}%
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default LowTankAlertsWidget;
